import { CATALOGUE } from '../data/catalogue';
import { PRICES, STOCK, NEXT_PRICE, NEXT_BATCH } from '../data/pricing';

// How long a unit we do not hold takes to arrive: "5 to 7 days", or a shorter
// time from NEXT_BATCH in data/pricing.js until its `until` date (that whole
// day, India time). Must match leadTimeFor() in scripts/product-offer.js.
export const USUAL_LEAD_TIME = '5 to 7 days';
export function leadTimeFor(modelId, kit, now = Date.now()) {
  const nb = NEXT_BATCH?.[modelId]?.[kit];
  if (!nb || !nb.days) return USUAL_LEAD_TIME;
  const end = nb.until ? Date.parse(`${nb.until}T23:59:59+05:30`) : Infinity;
  return now <= end ? `${nb.days} days` : USUAL_LEAD_TIME;
}

// ONE UNIT PER MODEL ONLINE (Oct 2026).
//
// The copper piping kit is no longer bought on the site. It is offered "at a
// discounted price on request" instead. So where a model exists both with and
// without the kit, only the without-kit unit is sold online, and the grid,
// the collection pages and the product page all show that one unit.
//
// Models that Daikin only makes with the kit (FTHT, FTKU, JTKJ, FTKL90) keep
// their with-kit unit, because that is the only one there is.
//
// THE ONE IN STOCK (Nov 2026). When the without-kit unit has none in stock
// and the with-kit unit has (FTKC50, FTKL71, FTKM60 on 02-11-2026), the
// with-kit unit is the one sold, so stock in hand is never hidden behind
// "Sold out". It goes back to the without-kit unit when that is restocked or
// the with-kit stock runs out.
//
// onlineKit() is the single place this rule lives. ProductDetail.jsx and
// scripts/product-offer.js (defaultKit) must agree with it, and so must the
// copies in scripts/check-catalogue.js, data/guideInverter.js and
// data/roomDay.js.

export function onlineKit(model) {
  const v = model.variants;
  if (!v.withoutKit) return 'withKit';
  const s = STOCK[model.id] || {};
  return v.withKit && !(s.withoutKit > 0) && s.withKit > 0 ? 'withKit' : 'withoutKit';
}

// SOLD OUT (Oct 2026). A STOCK entry of exactly 0 means every unit we held
// sold on the site, so the card and the product page say "Sold out" instead of
// a plain "on order". A missing entry is an ordinary on-order unit. See the
// STOCK note in data/pricing.js. scripts/product-offer.js and
// scripts/collection-body.js read STOCK the same way.
export function isSoldOut(modelId, kit) {
  return STOCK[modelId]?.[kit] === 0;
}

// What the next unit will cost, from NEXT_PRICE in data/pricing.js. Only
// while this stock lasts and only when it really is higher, so the "Last
// unit at this price" call-out never shows a claim that isn't true. Once the
// stock is gone the build moves PRICES to this number and the call-out goes.
export function nextPriceFor(modelId, kit) {
  const left = STOCK[modelId]?.[kit] || 0;
  const next = NEXT_PRICE?.[modelId]?.[kit];
  const now = PRICES[modelId]?.[kit];
  return left > 0 && typeof next === 'number' && typeof now === 'number' && next > now ? next : null;
}

// In stock, then sold out, then on order. Use it wherever a list is ordered
// by availability: sold-out units lead the on-order group, because "this sold
// out here" is worth seeing before a plain on-order card.
export const stockRank = (v) => (v.inStock ? 2 : v.soldOut ? 1 : 0);
export const byStock = (a, b) => stockRank(b) - stockRank(a);

export function listVariants() {
  const out = [];
  CATALOGUE.forEach((model) => {
    const kit = onlineKit(model);
    const price = PRICES[model.id]?.[kit];
    if (price == null) return;
    out.push({
      key: `${model.id}:${kit}`,
      model,
      kit,
      price,
      mrp: model.variants[kit].mrp,
      idu: model.variants[kit].idu,
      inStock: (STOCK[model.id]?.[kit] || 0) > 0,
      soldOut: isSoldOut(model.id, kit),
      hasSibling: false,
    });
  });

  // In stock first. These are the only units that can be bought and delivered
  // today — everything else is a request — so they lead the grid rather than
  // sitting wherever the catalogue happens to put them. Sold-out units come
  // next. Array.sort is stable, so catalogue order is preserved inside each
  // group.
  return out.sort(byStock);
}

export function filterVariants(variants, { ton, star, inverterOnly, kit, series }) {
  return variants.filter((v) => {
    if (ton && ton !== 'all' && v.model.soldAs !== ton) return false;
    if (star && star !== 'all' && v.model.stars !== Number(star)) return false;
    if (inverterOnly && !v.model.inverter) return false;
    if (kit && kit !== 'all' && v.kit !== kit) return false;
    if (series && v.model.series !== series) return false;
    return true;
  });
}

export const byPrice = (a, b) => a.price - b.price;

// Stock first, then cheapest inside each group. Use this wherever a page sorts
// explicitly, so an explicit sort does not bury the units you can actually
// ship tomorrow.
export const byStockThenPrice = (a, b) => byStock(a, b) || a.price - b.price;

export const kitLabel = (kit) => (kit === 'withKit' ? 'Piping kit included' : 'Without kit');